// Phase-0 spike harness (docs/prd/001 §4). Run at #/spike.
//
// Three things to learn, in one screen:
//   S1  Browser-direct CORS + auth for each provider (no server, user's key).
//   S2  Realtime diarization quality on your real room mic (eyeball the labels).
//   S3  That the getUserMedia -> AudioWorklet -> PCM -> WebSocket path works.
//
// Keys live ONLY in this browser's localStorage and go ONLY to the provider /
// Anthropic — there is no first-party server here, by design.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/utils'
import { createMicCapture, listMics, type MicCapture } from './audio'
import { probeAnthropic, probeSoniox, probeSpeechmatics } from './probes'
import { connectSoniox, mintSonioxTempKey } from './soniox'
import {
  connectSpeechmatics,
  mintSpeechmaticsJwt,
  type SpeechmaticsRegion
} from './speechmatics'
import type { ConnState, LiveSession, ProbeResult, Word } from './types'

const LS_PREFIX = 'convariance.spike.'
const SPEAKER_COLORS = [
  'text-accent',
  'text-success',
  'text-warn',
  'text-danger',
  'text-text',
  'text-muted-foreground'
] as const

type Path = 'speechmatics' | 'soniox-direct' | 'soniox-temp'

function lsGet(key: string): string {
  try {
    return localStorage.getItem(LS_PREFIX + key) ?? ''
  } catch {
    return ''
  }
}

function speakerColor(map: Map<string, number>, speaker: string): string {
  let idx = map.get(speaker)
  if (idx === undefined) {
    idx = map.size
    map.set(speaker, idx)
  }
  return SPEAKER_COLORS[idx % SPEAKER_COLORS.length] ?? 'text-text'
}

function groupBySpeaker(words: Word[]): { speaker: string; text: string }[] {
  const groups: { speaker: string; text: string }[] = []
  for (const w of words) {
    const last = groups[groups.length - 1]
    if (last?.speaker === w.speaker) {
      last.text += w.text.startsWith(' ') ? w.text : ` ${w.text}`
    } else {
      groups.push({ speaker: w.speaker, text: w.text })
    }
  }
  return groups
}

export function SpikePage() {
  const [smKey, setSmKey] = useState(() => lsGet('sm'))
  const [sxKey, setSxKey] = useState(() => lsGet('sx'))
  const [anKey, setAnKey] = useState(() => lsGet('an'))
  const [region, setRegion] = useState<SpeechmaticsRegion>('eu')

  const [probes, setProbes] = useState<{
    sm: ProbeResult | null
    sx: ProbeResult | null
    an: ProbeResult | null
  }>({ sm: null, sx: null, an: null })
  const [probing, setProbing] = useState<string | null>(null)

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [deviceId, setDeviceId] = useState('')

  const [connState, setConnState] = useState<ConnState>('idle')
  const [connDetail, setConnDetail] = useState('')
  const [activePath, setActivePath] = useState<Path | null>(null)
  const [pathLive, setPathLive] = useState<Record<Path, boolean>>({
    'speechmatics': false,
    'soniox-direct': false,
    'soniox-temp': false
  })

  const [finalWords, setFinalWords] = useState<Word[]>([])
  const [interimWords, setInterimWords] = useState<Word[]>([])
  const [level, setLevel] = useState(0)
  const [log, setLog] = useState<string[]>([])

  const captureRef = useRef<MicCapture | null>(null)
  const sessionRef = useRef<LiveSession | null>(null)

  const pushLog = useCallback((line: string) => {
    setLog((prev) => [...prev.slice(-120), line])
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem(LS_PREFIX + 'sm', smKey)
      localStorage.setItem(LS_PREFIX + 'sx', sxKey)
      localStorage.setItem(LS_PREFIX + 'an', anKey)
    } catch {
      // storage unavailable — keys just won't persist this session
    }
  }, [smKey, sxKey, anKey])

  const runProbe = useCallback(
    async (which: 'sm' | 'sx' | 'an') => {
      setProbing(which)
      pushLog(`probe ${which}: requesting…`)
      let result: ProbeResult
      try {
        if (which === 'sm') result = await probeSpeechmatics(smKey)
        else if (which === 'sx') result = await probeSoniox(sxKey)
        else result = await probeAnthropic(anKey)
      } catch (err) {
        result = {
          corsAllowed: false,
          authOk: false,
          detail: err instanceof Error ? err.message : String(err)
        }
      }
      setProbes((prev) => ({ ...prev, [which]: result }))
      pushLog(`probe ${which}: ${result.detail}`)
      setProbing(null)
    },
    [smKey, sxKey, anKey, pushLog]
  )

  const grantMic = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true })
      s.getTracks().forEach((t) => t.stop())
      const mics = await listMics()
      setDevices(mics)
      if (!deviceId && mics[0]) setDeviceId(mics[0].deviceId)
      pushLog(`mic granted — ${mics.length} input device(s)`)
    } catch (err) {
      pushLog(`mic error: ${err instanceof Error ? err.message : String(err)}`)
    }
  }, [deviceId, pushLog])

  const stop = useCallback(async () => {
    sessionRef.current?.stop()
    sessionRef.current = null
    await captureRef.current?.stop()
    captureRef.current = null
    setLevel(0)
  }, [])

  const start = useCallback(
    async (path: Path) => {
      await stop()
      setFinalWords([])
      setInterimWords([])
      setActivePath(path)
      setConnState('connecting')
      setConnDetail('starting mic')

      const events = {
        onState: (s: ConnState, detail?: string) => {
          setConnState(s)
          setConnDetail(detail ?? '')
        },
        onWords: (f: Word[], i: Word[]) => {
          setFinalWords([...f])
          setInterimWords([...i])
          // Mark the path proven only when transcription actually flows — proof
          // the server accepted key + config + audio end-to-end.
          if (f.length > 0 || i.length > 0) {
            setPathLive((prev) => (prev[path] ? prev : { ...prev, [path]: true }))
          }
        },
        onLog: pushLog
      }

      try {
        const cap = createMicCapture(deviceId || undefined)
        const rate = await cap.start(
          (pcm) => sessionRef.current?.sendPcm(pcm),
          (rms) => setLevel(rms)
        )
        captureRef.current = cap
        pushLog(`mic live @ ${rate} Hz`)

        let session: LiveSession
        if (path === 'speechmatics') {
          const jwt = await mintSpeechmaticsJwt(smKey)
          pushLog('speechmatics: JWT minted in-browser (CORS ok)')
          session = connectSpeechmatics({ jwt, region, sampleRate: rate }, events)
        } else if (path === 'soniox-direct') {
          session = connectSoniox({ key: sxKey, sampleRate: rate }, events)
        } else {
          const tempKey = await mintSonioxTempKey(sxKey)
          pushLog('soniox: temp key minted in-browser (CORS ok)')
          session = connectSoniox({ key: tempKey, sampleRate: rate }, events)
        }
        sessionRef.current = session
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        setConnState('error')
        setConnDetail(msg)
        pushLog(`start ${path} failed: ${msg}`)
        await stop()
      }
    },
    [deviceId, smKey, sxKey, region, pushLog, stop]
  )

  useEffect(() => {
    return () => {
      void stop()
    }
  }, [stop])

  const colorMap = new Map<string, number>()
  const finalGroups = groupBySpeaker(finalWords)
  const interimGroups = groupBySpeaker(interimWords)
  const running = connState === 'connecting' || connState === 'live'

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 md:px-6">
      <p className="text-body-sm text-muted-foreground">
        convariance · Phase-0 spike
      </p>
      <h1 className="mt-1 font-serif text-display-lg text-text">
        Browser-direct diarization probe
      </h1>
      <p className="mt-3 max-w-reading text-body text-muted-foreground">
        Tests whether the browser can reach each provider with only your own key
        and no server (S1), and how speaker diarization holds up on your real room
        mic (S2/S3). Keys stay in this browser and go only to the provider or
        Anthropic. See <code className="font-mono text-code">docs/prd/001</code>.
      </p>

      {/* Keys */}
      <Section title="Keys (stored locally only)">
        <div className="grid gap-3 md:grid-cols-3">
          <KeyField label="Speechmatics API key" value={smKey} onChange={setSmKey} />
          <KeyField label="Soniox API key" value={sxKey} onChange={setSxKey} />
          <KeyField label="Anthropic API key" value={anKey} onChange={setAnKey} />
        </div>
        <div className="mt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSmKey('')
              setSxKey('')
              setAnKey('')
            }}
          >
            Clear all keys
          </Button>
        </div>
      </Section>

      {/* S1 — CORS probes */}
      <Section title="S1 — Browser-direct CORS &amp; auth">
        <p className="mb-3 text-body-sm text-muted-foreground">
          A request that reaches the server (any HTTP status) proves CORS is
          allowed; a rejected fetch means CORS-blocked or offline. CORS-allowed
          and auth-ok are separate signals.
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          <ProbeCard
            name="Speechmatics token"
            result={probes.sm}
            busy={probing === 'sm'}
            disabled={!smKey}
            onRun={() => void runProbe('sm')}
          />
          <ProbeCard
            name="Soniox temp key"
            result={probes.sx}
            busy={probing === 'sx'}
            disabled={!sxKey}
            onRun={() => void runProbe('sx')}
          />
          <ProbeCard
            name="Anthropic messages"
            result={probes.an}
            busy={probing === 'an'}
            disabled={!anKey}
            onRun={() => void runProbe('an')}
          />
        </div>
      </Section>

      {/* S2/S3 — live diarization */}
      <Section title="S2 / S3 — Live diarization on the room mic">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void grantMic()}>
            Grant mic &amp; list devices
          </Button>
          {devices.length > 0 && (
            <select
              className="h-8 rounded-md border border-input bg-transparent px-2 text-body-sm text-text"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
            >
              {devices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || `mic ${d.deviceId.slice(0, 6)}`}
                </option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-1 text-body-sm text-muted-foreground">
            SM region
            <select
              className="h-8 rounded-md border border-input bg-transparent px-2 text-body-sm text-text"
              value={region}
              onChange={(e) => setRegion(e.target.value as SpeechmaticsRegion)}
            >
              <option value="eu">eu</option>
              <option value="us">us</option>
            </select>
          </label>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!smKey || running}
            onClick={() => void start('speechmatics')}
          >
            Start Speechmatics
          </Button>
          <Button
            size="sm"
            disabled={!sxKey || running}
            onClick={() => void start('soniox-direct')}
          >
            Start Soniox (direct key)
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!sxKey || running}
            onClick={() => void start('soniox-temp')}
          >
            Start Soniox (temp key)
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={!running}
            onClick={() => void stop()}
          >
            Stop
          </Button>
        </div>

        {/* status + level */}
        <div className="mt-3 flex items-center gap-3">
          <StateBadge state={connState} />
          <span className="text-body-sm text-muted-foreground">
            {activePath ? `${activePath} · ` : ''}
            {connDetail}
          </span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-sidebar">
          <div
            className="h-full bg-accent transition-[width] duration-75"
            style={{ width: `${Math.min(100, Math.round(level * 300))}%` }}
          />
        </div>

        {/* transcript */}
        <div className="mt-4 h-64 overflow-y-auto rounded-lg border border-border bg-surface p-4">
          {finalGroups.length === 0 && interimGroups.length === 0 ? (
            <p className="text-body-sm text-muted-foreground">
              No transcript yet. Grant the mic, start a provider, and speak — watch
              whether the speaker labels stay stable per person.
            </p>
          ) : (
            <div className="space-y-1.5 text-body">
              {finalGroups.map((g, i) => (
                <p key={`f${i}`}>
                  <SpeakerTag color={speakerColor(colorMap, g.speaker)}>
                    {g.speaker}
                  </SpeakerTag>{' '}
                  <span className="text-text">{g.text}</span>
                </p>
              ))}
              {interimGroups.map((g, i) => (
                <p key={`i${i}`} className="opacity-50">
                  <SpeakerTag color={speakerColor(colorMap, g.speaker)}>
                    {g.speaker}
                  </SpeakerTag>{' '}
                  <span className="italic text-muted-foreground">{g.text}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* Verdict */}
      <Section title="Verdict (what the spike found)">
        <ul className="space-y-2">
          <Verdict
            ok={probes.an?.corsAllowed ?? null}
            label="Anthropic browser-direct (BYOK)"
            yes="CORS allowed — Claude is callable from the browser."
            no="CORS blocked — Claude would need a proxy."
          />
          <Verdict
            ok={probes.sm?.corsAllowed ?? null}
            label="Speechmatics token mint in-browser"
            yes="CORS allowed — pure-browser Speechmatics is viable."
            no="CORS blocked — Speechmatics needs a tiny token-minting endpoint."
          />
          <Verdict
            ok={pathLive['soniox-direct'] ? true : null}
            label="Soniox direct-key, server-free path"
            yes="Reached LIVE with the raw key in the WS config — no server, no token mint."
            no="Not yet confirmed live. Run “Start Soniox (direct key)”."
          />
        </ul>
      </Section>

      {/* Log */}
      <Section title="Event log">
        <div className="h-40 overflow-y-auto rounded-lg border border-border bg-sidebar p-3 font-mono text-code text-muted-foreground">
          {log.length === 0 ? (
            <span>No events yet.</span>
          ) : (
            log.map((line, i) => <div key={i}>{line}</div>)
          )}
        </div>
      </Section>
    </div>
  )
}

function Section({
  title,
  children
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 text-heading-3 font-medium text-text">{title}</h2>
      {children}
    </section>
  )
}

function KeyField({
  label,
  value,
  onChange
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-label text-muted-foreground">{label}</span>
      <Input
        type="password"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="paste key"
      />
    </label>
  )
}

function ProbeCard({
  name,
  result,
  busy,
  disabled,
  onRun
}: {
  name: string
  result: ProbeResult | null
  busy: boolean
  disabled: boolean
  onRun: () => void
}) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <div className="flex items-center justify-between">
        <span className="text-body-sm font-medium text-text">{name}</span>
        <Button size="xs" variant="outline" disabled={disabled || busy} onClick={onRun}>
          {busy ? 'testing…' : 'test'}
        </Button>
      </div>
      {result && (
        <div className="mt-2 space-y-1">
          <div className="flex gap-1.5">
            <Badge variant={result.corsAllowed ? 'default' : 'destructive'}>
              {result.corsAllowed ? 'CORS ok' : 'CORS blocked'}
            </Badge>
            <Badge variant={result.authOk ? 'default' : 'outline'}>
              {result.authOk ? 'auth ok' : `auth ${result.status ?? '—'}`}
            </Badge>
          </div>
          <p className="text-label text-muted-foreground">{result.detail}</p>
        </div>
      )}
    </div>
  )
}

function StateBadge({ state }: { state: ConnState }) {
  const variant =
    state === 'live'
      ? 'default'
      : state === 'error'
        ? 'destructive'
        : 'secondary'
  return <Badge variant={variant}>{state}</Badge>
}

function SpeakerTag({
  color,
  children
}: {
  color: string
  children: React.ReactNode
}) {
  return (
    <span className={cn('font-mono text-code font-semibold', color)}>
      {children}
    </span>
  )
}

function Verdict({
  ok,
  label,
  yes,
  no
}: {
  ok: boolean | null
  label: string
  yes: string
  no: string
}) {
  const variant = ok === null ? 'secondary' : ok ? 'default' : 'destructive'
  return (
    <li className="flex items-start gap-2">
      <Badge variant={variant}>{ok === null ? '?' : ok ? 'yes' : 'no'}</Badge>
      <span className="text-body-sm text-text">
        <span className="font-medium">{label}:</span>{' '}
        <span className="text-muted-foreground">{ok === null ? 'not run' : ok ? yes : no}</span>
      </span>
    </li>
  )
}
